import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { format } from 'date-fns';
import MainLayout from '../components/MainLayout';
import { useAppSelector } from '../hooks/redux';
import SupportTicketModal from '../components/SupportTicketModal';

interface Attachment {
  filename: string;
  originalName: string;
  s3Key: string;
  s3Url: string;
  cloudFrontUrl: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
}

interface SupportTicket {
  id: string;
  firebaseUid: string;
  name: string;
  email: string;
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  attachments: Attachment[];
  adminNotes?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  createdAt: string;
  updatedAt: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const SupportTickets: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTicket, setEditingTicket] = useState<SupportTicket | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // View mode
  const [viewMode, setViewMode] = useState<'all' | 'my-tickets'>('all');

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');

  // Pagination
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 15,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filterStatus, filterSeverity, viewMode]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });

      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }

      if (filterSeverity !== 'all') {
        params.append('severity', filterSeverity);
      }

      // Choose endpoint based on view mode
      const endpoint = viewMode === 'my-tickets'
        ? `/admin/support-tickets/my-tickets?${params}`
        : `/admin/support-tickets?${params}`;

      const response = await apiService.get(endpoint);

      if (response?.success) {
        setTickets(response.data.tickets || []);
        setPagination(response.data.pagination || {
          page: 1,
          limit: 15,
          total: 0,
          pages: 0
        });
      }
    } catch (err: any) {
      console.error('Failed to fetch support tickets:', err);
      setError(err.message || 'Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleModalSubmit = async (data: { title: string; description: string; severity: 'low' | 'medium' | 'high'; files: File[]; filesToDelete?: string[] }) => {
    try {
      setSubmitting(true);
      setError(null);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('severity', data.severity);

      // Add files
      data.files.forEach(file => {
        formData.append('attachments', file);
      });

      // Add files to delete (if any)
      if (data.filesToDelete && data.filesToDelete.length > 0) {
        formData.append('filesToDelete', JSON.stringify(data.filesToDelete));
      }

      let response;
      if (editingTicket) {
        // Update existing ticket
        response = await apiService.putFormData(`/admin/support-tickets/my-tickets/${editingTicket.id}`, formData);
        setSuccess('Support ticket updated successfully');
      } else {
        // Create new ticket
        response = await apiService.postFormData('/admin/support-tickets', formData);
        setSuccess('Support ticket created successfully');
      }

      if (response?.success) {
        setShowModal(false);
        setEditingTicket(null);
        fetchTickets();

        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err: any) {
      console.error('Failed to submit support ticket:', err);
      setError(err.message || 'Failed to submit support ticket');
      throw err; // Re-throw to let modal handle it
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTicket = (ticket: SupportTicket) => {
    // Only allow users to edit their own tickets
    if (user && ticket.firebaseUid !== user.uid) {
      setError('You can only edit your own support tickets');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setEditingTicket(ticket);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTicket(null);
    setError(null);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-purple-100 text-purple-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <MainLayout title="Support Tickets">
      <div className="p-2">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Support Tickets</h1>
            <p className="text-gray-600 mt-1">Manage and track support requests</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            + Create Ticket
          </button>
        </div>

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        {error && !showModal && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* View Mode Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px space-x-8">
              <button
                onClick={() => {
                  setViewMode('all');
                  setPagination({ ...pagination, page: 1 });
                }}
                disabled={loading}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                  ${viewMode === 'all'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <span className="flex items-center gap-2">
                  All Tickets
                  {viewMode === 'all' && loading && (
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                  )}
                  {viewMode === 'all' && !loading && (
                    <span className="bg-blue-100 text-blue-600 py-0.5 px-2 rounded-full text-xs font-semibold">
                      {pagination.total}
                    </span>
                  )}
                </span>
              </button>
              <button
                onClick={() => {
                  setViewMode('my-tickets');
                  setPagination({ ...pagination, page: 1 });
                }}
                disabled={loading}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                  ${viewMode === 'my-tickets'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <span className="flex items-center gap-2">
                  My Tickets
                  {viewMode === 'my-tickets' && loading && (
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                  )}
                  {viewMode === 'my-tickets' && !loading && (
                    <span className="bg-blue-100 text-blue-600 py-0.5 px-2 rounded-full text-xs font-semibold">
                      {pagination.total}
                    </span>
                  )}
                </span>
              </button>
            </nav>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value)}
              >
                <option value="all">All Severity</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <button
              onClick={fetchTickets}
              className="self-end px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Tickets Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading tickets...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ticket Info
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Severity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Attachments
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tickets.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                          No support tickets found
                        </td>
                      </tr>
                    ) : (
                      tickets.map((ticket) => {
                        const isOwnTicket = user && ticket.firebaseUid === user.uid;
                        return (
                        <tr
                          key={ticket.id}
                          onClick={() => handleEditTicket(ticket)}
                          className={`transition-colors ${isOwnTicket ? 'cursor-pointer hover:bg-blue-50' : 'cursor-default opacity-75'}`}
                        >
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">
                              {ticket.title}
                            </div>
                            <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                              {ticket.description}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{ticket.name}</div>
                            <div className="text-xs text-gray-500">{ticket.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(ticket.severity)}`}>
                              {ticket.severity.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(ticket.status)}`}>
                              {ticket.status.replace('_', ' ').toUpperCase()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {ticket.attachments && ticket.attachments.length > 0 ? (
                              <div className="space-y-1">
                                {ticket.attachments.map((attachment, index) => (
                                  <a
                                    key={index}
                                    href={attachment.cloudFrontUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center text-xs text-blue-600 hover:text-blue-800"
                                  >
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                    </svg>
                                    {attachment.originalName}
                                    <span className="ml-1 text-gray-400">
                                      ({formatFileSize(attachment.fileSize)})
                                    </span>
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">No attachments</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                            {format(new Date(ticket.createdAt), 'MMM dd, yyyy HH:mm')}
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.total > 0 && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                      {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                      {pagination.total} results
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={pagination.page === 1}
                        className="px-3 py-1 border border-gray-300 rounded-md bg-white text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={pagination.page === pagination.pages}
                        className="px-3 py-1 border border-gray-300 rounded-md bg-white text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Support Ticket Modal */}
        <SupportTicketModal
          isOpen={showModal}
          onClose={handleCloseModal}
          onSubmit={handleModalSubmit}
          ticket={editingTicket}
          submitting={submitting}
          error={error}
        />
      </div>
    </MainLayout>
  );
};

export default SupportTickets;
